import { Link } from "react-router-dom";
import { Sparkles, Gem, Moon, Sun, Sunrise, Copy, RotateCcw, CalendarCheck, FileDown, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Stagger, StaggerItem } from "@/components/Motion";
import NatalWheel from "@/components/NatalWheel";
import BookingDialog from "@/components/BookingDialog";
import { API } from "@/lib/api";
import { track } from "@/lib/analytics";

const SESSION_SERVICE = "Sessione Tema Natale — Talenti e Doni";
const T = "\uFE0E";
const sym = { fontFamily: "'Segoe UI Symbol','Noto Sans Symbols 2','Noto Sans Symbols','DejaVu Sans','Apple Symbols',serif" };

function BigThree({ chart }) {
  const get = (n) => chart.planets.find((p) => p.name === n);
  const items = [
    { icon: Sun, label: "Sole", sub: "La tua essenza", v: get("Sole") },
    { icon: Moon, label: "Luna", sub: "Il tuo mondo emotivo", v: get("Luna") },
    { icon: Sunrise, label: "Ascendente", sub: "Come ti presenti al mondo", v: chart.angles?.Ascendente },
  ].filter((i) => i.v);
  return (
    <Stagger className="grid sm:grid-cols-3 gap-4" >
      {items.map(({ icon: Icon, label, sub, v }) => (
        <StaggerItem key={label}>
          <div className="glass rounded-2xl p-6 h-full" data-testid={`big-three-${label.toLowerCase()}`}>
            <div className="flex items-center gap-2 text-gold mb-3"><Icon size={16} /><span className="text-xs uppercase tracking-[0.2em]">{label}</span></div>
            <p className="font-display text-3xl text-[#2A1F14]"><span style={sym}>{v.sign_symbol + T}</span> {v.sign}</p>
            <p className="text-[#2A1F14]/50 text-sm mt-1">{v.degree.toFixed(0)}° · {v.element} · {v.modality}</p>
            <p className="text-[#2A1F14]/60 text-xs mt-3">{sub}</p>
          </div>
        </StaggerItem>
      ))}
    </Stagger>
  );
}

function Elements({ chart }) {
  const total = Object.values(chart.elements).reduce((a, b) => a + b, 0) || 1;
  return (
    <div className="space-y-3" data-testid="elements-balance">
      {Object.entries(chart.elements).map(([k, v]) => (
        <div key={k}>
          <div className="flex justify-between text-xs text-[#2A1F14]/60 mb-1"><span>{k}</span><span>{Math.round((v / total) * 100)}%</span></div>
          <div className="h-1.5 rounded-full bg-[#2A1F14]/8 overflow-hidden"><div className="h-full bg-gold rounded-full transition-all duration-1000" style={{ width: `${(v / total) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

export default function NatalResults({ data, onReset }) {
  const { chart, interpretation: it, name } = data;
  const sun = chart.planets.find((p) => p.name === "Sole");
  const moon = chart.planets.find((p) => p.name === "Luna");
  const asc = chart.angles?.Ascendente;
  const dateIt = data.birth_date.split("-").reverse().join("/");
  const summary = `Tema natale di ${name}: Sole in ${sun.sign}, Luna in ${moon.sign}${asc ? `, Ascendente ${asc.sign}` : ""}. Nato/a il ${dateIt}${data.birth_time ? ` alle ${data.birth_time}` : ""} a ${data.place}.` + (it?.sessione ? ` Sessione proposta: ${it.sessione.titolo}.` : "");

  const copyLink = () => {
    const url = `${window.location.origin}/tema-natale?id=${data.id}`;
    const done = () => toast.success("Link copiato");
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(done).catch(() => fallbackCopy(url, done));
    else fallbackCopy(url, done);
  };
  const fallbackCopy = (text, done) => {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); done(); } catch (_) { toast(text); }
    document.body.removeChild(ta);
  };

  return (
    <div className="space-y-20" data-testid="natal-results">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Il tema natale di {name}</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2A1F14] leading-[1.05] max-w-3xl mb-6" data-testid="natal-title">{it?.titolo || "Il cielo della tua nascita"}</h2>
        <p className="text-[#2A1F14]/50 text-sm mb-8">{dateIt}{data.birth_time ? ` · ${data.birth_time}` : " · ora non nota"} · {data.place}</p>
        <div className="flex flex-wrap gap-3 mb-10">
          <a href={`${API}/natal-chart/${data.id}/pdf`} target="_blank" rel="noreferrer" onClick={() => track("natal_pdf")} data-testid="natal-download-pdf"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2A1F14] text-[#F7EFE0] text-sm font-medium hover:bg-gold hover:text-black transition-colors">
            <FileDown size={16} /> Scarica il tuo Tema in PDF
          </a>
          <button onClick={copyLink} data-testid="natal-copy-link" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#2A1F14]/20 text-[#2A1F14]/75 text-sm hover:border-gold hover:text-gold transition-colors"><Copy size={15} /> Copia il link</button>
        </div>
        <BigThree chart={chart} />
      </Reveal>

      {it?.essenza && (
        <Reveal className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Essenza</p>
          <p className="text-[#2A1F14]/80 text-lg sm:text-xl leading-relaxed font-light" data-testid="natal-essence">{it.essenza}</p>
        </Reveal>
      )}

      <Reveal className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-start">
        <div className="max-w-[520px] mx-auto w-full"><NatalWheel chart={chart} /></div>
        <div className="space-y-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Pianeti</p>
            <table className="w-full text-sm" data-testid="planets-table">
              <tbody>
                {chart.planets.map((p) => (
                  <tr key={p.name} className="border-b border-[#2A1F14]/8">
                    <td className="py-2 text-[#2A1F14]/50 w-8" style={sym}>{p.symbol + T}</td>
                    <td className="py-2 text-[#2A1F14]">{p.name}{p.retrograde && <span className="text-gold text-xs ml-1">℞</span>}</td>
                    <td className="py-2 text-[#2A1F14]/75"><span style={sym}>{p.sign_symbol + T}</span> {p.sign} {p.degree.toFixed(0)}°</td>
                    <td className="py-2 text-[#2A1F14]/45 text-right">{p.house ? `Casa ${p.house}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Equilibrio degli elementi</p>
            <Elements chart={chart} />
          </div>
        </div>
      </Reveal>

      {it?.talenti?.length > 0 && (
        <div>
          <Reveal><p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Rivelazioni</p><h3 className="font-display text-3xl sm:text-4xl text-[#2A1F14] mb-10">I tuoi talenti.</h3></Reveal>
          <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A1F14]/10 rounded-2xl overflow-hidden" >
            {it.talenti.map((t, i) => (
              <StaggerItem key={i}>
                <div className="bg-[#F7EFE0] p-8 h-full" data-testid="talent-card">
                  <span className="font-display text-gold text-2xl">0{i + 1}</span>
                  <h4 className="font-display text-2xl text-[#2A1F14] mt-3 mb-3">{t.nome}</h4>
                  <p className="text-[#2A1F14]/65 text-sm leading-relaxed">{t.descrizione}</p>
                  {t.origine && <p className="text-gold/80 text-xs mt-4 tracking-wide">{t.origine}</p>}
                </div>
              </StaggerItem>
            ))}
            {it.talenti.length % 3 !== 0 && (
              <StaggerItem>
                <div className="bg-[#EFE3CE] p-8 h-full flex flex-col justify-end" data-testid="talent-filler">
                  <Sparkles size={20} className="text-gold mb-4" />
                  <p className="font-display text-2xl text-[#2A1F14] leading-snug">Ogni talento è una porta. La sessione personalizzata ti mostra come attraversarla.</p>
                </div>
              </StaggerItem>
            )}
          </Stagger>
        </div>
      )}

      {it?.doni?.length > 0 && (
        <div className="bg-[#EFE3CE] rounded-3xl p-8 sm:p-12">
          <Reveal><p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Doni dell'anima</p><h3 className="font-display text-3xl sm:text-4xl text-[#2A1F14] mb-10">Ciò che porti in dono.</h3></Reveal>
          <Stagger className="grid md:grid-cols-3 gap-8">
            {it.doni.map((d, i) => (
              <StaggerItem key={i}>
                <div data-testid="gift-card">
                  <Gem size={20} className="text-gold mb-4" />
                  <h4 className="font-display text-xl text-[#2A1F14] mb-2">{d.nome}</h4>
                  <p className="text-[#2A1F14]/65 text-sm leading-relaxed">{d.descrizione}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          {it.ombra && (
            <Reveal className="mt-12 pt-10 border-t border-[#2A1F14]/10 max-w-3xl">
              <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">L'ombra da integrare</p>
              <p className="text-[#2A1F14]/75 leading-relaxed" data-testid="shadow-text">{it.ombra}</p>
            </Reveal>
          )}
        </div>
      )}

      {it?.sessione && (
        <Reveal><Link to={`/quiz?chart=${data.id}`} data-testid="natal-quiz-cta" className="group block rounded-3xl bg-[#2A1F14] text-[#F7EFE0] p-8 sm:p-12 relative overflow-hidden hover:bg-[#3a2b1c] transition-colors">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full border border-gold/20" />
          <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center relative">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Sbloccato · Quiz Spirituale</p>
              <h3 className="font-display text-3xl sm:text-4xl leading-tight mb-4">Trenta domande scritte sul tuo cielo.</h3>
              <p className="text-[#F7EFE0]/65 leading-relaxed max-w-2xl">Dalla più semplice alla più avanzata, ARIES misura la tua conoscenza spirituale a partire da {sun.sign}, {moon.sign}{asc ? ` e Ascendente ${asc.sign}` : ""}. Alla fine: voto, grado e un percorso su misura tra sessione e corsi Lux Academy.</p>
            </div>
            <span className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-black text-sm font-medium group-hover:bg-[#D4A056] transition-colors whitespace-nowrap">{data.quiz_score != null ? `Rivedi: ${data.quiz_score}/100 · ${data.quiz_grade}` : "Inizia il quiz"} <ArrowRight size={16} /></span>
          </div>
        </Link></Reveal>
      )}

      {it?.sessione && (
        <Reveal><div className="relative overflow-hidden rounded-3xl glass p-8 sm:p-12 constellation" data-testid="session-card">
          <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4"><Sparkles size={14} /> La tua sessione personalizzata</div>
              <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#2A1F14] leading-tight mb-6">{it.sessione.titolo}</h3>
              <p className="text-[#2A1F14]/70 leading-relaxed mb-6">{it.sessione.descrizione}</p>
              <ul className="space-y-2 mb-8">
                {(it.sessione.focus || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#2A1F14]/80"><span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 shrink-0" />{f}</li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3">
                <BookingDialog
                  title={`Prenota — ${SESSION_SERVICE}`}
                  source="tema-natale"
                  presets={{ type: "servizio", service: SESSION_SERVICE, name, message: `${summary}\n\nVorrei prenotare la sessione personalizzata "${it.sessione.titolo}".` }}
                  trigger={<button data-testid="natal-book-session" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors"><CalendarCheck size={16} /> Prenota la tua sessione · 75 min</button>}
                />
                {it.sessione.servizio_slug && (
                  <Link to={`/servizi/${it.sessione.servizio_slug}`} data-testid="natal-related-service" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-[#2A1F14]/20 text-[#2A1F14]/75 text-sm hover:border-gold hover:text-gold transition-colors">
                    Approfondisci: {it.sessione.servizio_titolo || "servizio affine"}
                  </Link>
                )}
              </div>
            </div>
            <div className="hidden lg:block text-center">
              <p className="font-display text-[7rem] leading-none text-gold/30" style={sym}>{sun.sign_symbol + T}</p>
              <p className="text-xs uppercase tracking-[0.3em] text-[#2A1F14]/40">Costruita sul tuo cielo</p>
            </div>
          </div>
        </div></Reveal>
      )}

      {!it && (
        <div className="glass rounded-2xl p-8 text-center" data-testid="natal-no-interpretation">
          <p className="text-[#2A1F14]/70 mb-6">Il tema è stato calcolato, ma la lettura dei talenti non è al momento disponibile. Puoi comunque prenotare una sessione personalizzata.</p>
          <BookingDialog title={`Prenota — ${SESSION_SERVICE}`} source="tema-natale" presets={{ type: "servizio", service: SESSION_SERVICE, name, message: summary }}
            trigger={<button data-testid="natal-book-session" className="px-7 py-3.5 rounded-full bg-gold text-black text-sm font-medium">Prenota la sessione</button>} />
        </div>
      )}

      <div className="flex flex-wrap gap-4 justify-center pt-4">
        <a href={`${API}/natal-chart/${data.id}/pdf`} target="_blank" rel="noreferrer" onClick={() => track("natal_pdf")} data-testid="natal-download-pdf-bottom" className="inline-flex items-center gap-2 text-sm text-[#2A1F14]/60 hover:text-gold transition-colors"><FileDown size={15} /> Scarica il PDF</a>
        <button onClick={copyLink} data-testid="natal-copy-link-bottom" className="inline-flex items-center gap-2 text-sm text-[#2A1F14]/60 hover:text-gold transition-colors"><Copy size={15} /> Copia il link del tuo tema</button>
        <button onClick={onReset} data-testid="natal-reset" className="inline-flex items-center gap-2 text-sm text-[#2A1F14]/60 hover:text-gold transition-colors"><RotateCcw size={15} /> Calcola un altro tema</button>
      </div>
    </div>
  );
}
